import {
    Component,
    ComponentConstructor,
    Object as Object3D,
    Physics,
    I18N,
    MorphTargets,
    Texture,
    Font,
    GraphicsApi,
    AudioClip,
} from './wonderland.js';

import {Emitter, RetainEmitter} from './utils/event.js';
import {isString} from './utils/object.js';
import {APIVersion, Version} from './version.js';
import {WASM} from './wasm.js';
import {Logger} from './utils/logger.js';
import {clamp} from './utils/misc.js';
import {MaterialManager} from './resources/material-manager.js';
import {MeshManager} from './resources/mesh-manager.js';
import {ResourceManager, ResourceType, SceneResource} from './resources/resource.js';
import {needsFlipY, TextureManager} from './resources/texture-manager.js';
import {LogTag} from './wonderland.js';
import {Constructor, ImageLike, ProgressCallback} from './types.js';
import {XRSessionState, WebXR} from './webxr.js';
import {Prefab, InMemoryLoadOptions, LoadOptions, StreamLoadOptions} from './prefab.js';
import {Scene, ActivateOptions, ChunkedSceneLoadSink, SceneType} from './scene.js';
import {PrefabGLTF, GLTFOptions} from './scene-gltf.js';
import {ArrayBufferSource, fetchStreamWithProgress} from './utils/fetch.js';
import {ProbeVolumeScenarioManager} from './resources/volume-scenario-manager.js';
import {ParticleEffectManager} from './resources/particle-effect-manager.js';
import {PipelineManager} from './resources/pipeline-manager.js';
import {createDestroyedProxy} from './utils/internal.js';

/**
 * Ensures that this API is compatible with the given
 * runtime version.
 *
 * We only enforce compatibility for major and minor components, i.e.,
 * the runtime and the API must both be of the form `x.y.*`.
 *
 * @throws If the major or the minor components are different.
 *
 * @param version The target version
 */
export function checkRuntimeCompatibility(version: Version) {
    const {major, minor} = version;

    let majorDiff = major - APIVersion.major;
    let minorDiff = minor - APIVersion.minor;
    /* Same version, so perfectly compatible. */
    if (!majorDiff && !minorDiff) return;

    const error =
        'checkRuntimeCompatibility(): Version compatibility mismatch:\n' +
        '\t→ API and runtime compatibility is enforced on a patch level (versions x.y.*)\n';

    const isRuntimeOlder = majorDiff < 0 || (!majorDiff && minorDiff < 0);
    if (isRuntimeOlder) {
        /* Runtime is out of date. */
        throw new Error(
            `${error}\t→ Please use a Wonderland Engine editor version >= ${APIVersion.major}.${APIVersion.minor}.*`
        );
    }
    /* API is out of date. */
    throw new Error(
        `${error}\t→ Please use a new API version >= ${version.major}.${version.minor}.*`
    );
}

/**
 * Reload the browser tab.
 *
 * @note This method first ensures the XR session is terminated,
 * to avoid blocking the Oculus Quest page.
 *
 * @param engine The engine instance
 */
function reloadPage(engine: WonderlandEngine) {
    /* End XR session for Oculus Quest blocking reload otherwise */
    const session = engine.xr?.session ?? null;
    if (!session) {
        location.reload();
        return;
    }
    session.end().then(() => location.reload());
}

enum JobStatus {
    Failure = 0,
    Success = 1,
}

/** Proxy used to override prototypes of destroyed prefabs. */
const DestroyedPrefabInstance = createDestroyedProxy('prefab/scene');

/**
 * Main Wonderland Engine instance.
 *
 * Controls the canvas, rendering, and JS <-> WASM communication.
 */
export class WonderlandEngine {
    /**
     * {@link Emitter} for WebXR session end events.
     *
     * Usage from within a component:
     *
     * ```js
     * this.engine.onXRSessionEnd.add(() => console.log("XR session ended."));
     * ```
     */
    get onXRSessionEnd(): Emitter {
        return this.#webxr.onSessionEnd;
    }

    /**
     * {@link RetainEmitter} for WebXR session start events.
     *
     * Usage from within a component:
     *
     * ```js
     * this.engine.onXRSessionStart.add((session, mode) => console.log(session, mode));
     * ```
     *
     * By default, this emitter is retained and will automatically call any callback added
     * while a session is already started:
     *
     * ```js
     * // XR session is already active.
     * this.engine.onXRSessionStart.add((session, mode) => {
     *     console.log(session, mode); // Triggered immediately.
     * });
     * ```
     */
    get onXRSessionStart(): Emitter<[XRSession, XRSessionMode]> {
        return this.#webxr.onSessionStart;
    }

    /**
     * {@link Emitter} for canvas / main framebuffer resize events.
     *
     * Usage from within a component:
     *
     * ```js
     * this.engine.onResize.add(() => {
     *     const canvas = this.engine.canvas;
     *     console.log(`New Size: ${canvas.width}, ${canvas.height}`);
     * });
     * ```
     *
     * @note The size of the canvas is in physical pixels, and is set via {@link WonderlandEngine.resize}.
     */
    readonly onResize: Emitter = new Emitter();

    /** Whether AR is supported by the browser. */
    get arSupported(): boolean {
        return this.#webxr.arSupported;
    }

    /** Whether VR is supported by the browser. */
    get vrSupported(): boolean {
        return this.#webxr.vrSupported;
    }

    /**
     * {@link RetainEmitter} signalling the end of the loading screen.
     *
     * Listeners get notified when the first call to {@link Scene#load()} is
     * invoked. At this point the new scene has not become active, and none of
     * its resources or components are initialized.
     *
     * Compared to {@link onSceneLoaded}, this does not wait for all components
     * to be fully initialized and activated. Any handler added inside
     * {@link Component#init()}, {@link Component#start()} or
     * {@link Component#onActivate()} will be called immediately.
     *
     * Usage:
     *
     * ```js
     * this.engine.onLoadingScreenEnd.add(() => console.log("Wait is over!"));
     * ```
     */
    readonly onLoadingScreenEnd = new RetainEmitter();

    /**
     * {@link Emitter} for scene loaded events.
     *
     * Listeners get notified when a call to {@link Scene#load()} finishes. At
     * this point all resources are loaded and all components had their
     * {@link Component#init()} as well as (if active)
     * {@link Component#start()} and {@link Component#onActivate()} methods
     * called.
     *
     * Usage from within a component:
     *
     * ```js
     * this.engine.onSceneLoaded.add(() => console.log("Scene switched!"));
     * ```
     *
     * @deprecated Use {@link onSceneActivated} instead.
     */
    readonly onSceneLoaded = new Emitter();

    /**
     * {@link Emitter} for scene activated events.
     *
     * This listener is notified with the old scene as first parameter, and
     * the new scene as second.
     *
     * This listener is notified after all resources are loaded and all components had their
     * {@link Component#init()} as well as (if active)
     * {@link Component#start()} and {@link Component#onActivate()} methods
     * called.
     *
     * Usage from within a component:
     *
     * ```js
     * this.engine.onSceneActivated.add((oldScene, newScene) => {
     *     console.log(`Scene switch from ${oldScene.filename} to ${newScene.filename}`);
     * });
     * ```
     */
    readonly onSceneActivated = new Emitter<[Scene, Scene]>();

    /**
     * Triggered when a hot reload is requested.
     *
     * When using the Wonderland Editor, this will be triggered when packaging
     * the project.
     *
     * @note When {@link autoHotReload} is `true`, the emitter is notified after the
     * scene is reloaded.
     *
     * To customize the hot reload behavior, set the {@link autoHotReload} to `false`,
     * and use this emitter:
     *
     * ```js
     * this.engine.onHotReloadRequest.add(async (filename: string) => {
     *     // This handler can be used to re-download a scene, download components,
     *     // fetch an API, anything!
     *     await this.loadMainScene(filename);
     * });
     * ```
     */
    readonly onHotReloadRequest: Emitter<[string]> = new Emitter();

    /**
     * Access to internationalization.
     */
    readonly i18n: I18N = new I18N(this);

    /**
     * WebXR related state, `null` if no XR session is active.
     */
    get xr(): XRSessionState | null {
        return this.#webxr.sessionState;
    }

    /**
     * If `true`, {@link Texture}, {@link Object3D}, and {@link Component}
     * instances have their prototype erased upon destruction.
     *
     * #### Object
     *
     * ```js
     * engine.erasePrototypeOnDestroy = true;
     *
     * const obj = engine.scene.addObject();
     * obj.name = 'iamalive';
     * console.log(obj.name); // Prints 'iamalive'
     *
     * obj.destroy();
     * console.log(obj.name); // Throws an error
     * ```
     *
     * #### Component
     *
     * Components will also be affected:
     *
     * ```js
     * class MyComponent extends Component {
     *     static TypeName = 'my-component';
     *     static Properties = {
     *         alive: Property.bool(true)
     *     };
     *
     *     start() {
     *         this.destroy();
     *         console.log(this.alive) // Throws an error
     *     }
     * }
     * ```
     *
     * A component is also destroyed if its ancestor gets destroyed:
     *
     * ```js
     * class MyComponent extends Component {
     *     ...
     *     start() {
     *         this.object.parent.destroy();
     *         console.log(this.alive) // Throws an error
     *     }
     * }
     * ```
     *
     * @note Native components will not be erased if destroyed via object destruction:
     *
     * ```js
     * const mesh = obj.addComponent('mesh');
     * obj.destroy();
     * console.log(mesh.active) // Doesn't throw even if the mesh is destroyed
     * ```
     *
     * @since 1.1.1
     */
    erasePrototypeOnDestroy = false;

    /**
     * If `true`, the materials will be wrapped in a proxy to support pre-1.2.0
     * material access, i.e.,
     *
     * ```js
     * const material = new Material(engine, 'Phong Opaque');
     * material.diffuseColor = [1.0, 0.0, 0.0, 1.0];
     * ```
     *
     * If `false`, property accessors will not be available and material
     * properties should be accessed via methods, i.e.,
     *
     * ```js
     * const PhongOpaque = engine.materials.getTemplate('Phong Opaque');
     * const material = new PhongOpaque();
     * material.setDiffuseColor([1.0, 0.0, 0.0, 1.0]);
     * ...
     * const diffuse = material.getDiffuseColor();
     * ```
     *
     * When disabled, reading/writing to materials is slightly more efficient on the CPU.
     */
    legacyMaterialSupport = true;

    /**
     * If `true`, automatically reloads the scene sent by the editor socket.
     *
     * @note The {@link onHotReloadRequest} emitter is notified **after** the scene is re-loaded.
     *
     * To use custom hot-reload code, set this boolean to `false` and add the reloading code
     *
     */
    autoHotReload = true;

    /**
     * If `true`, uncompressed images will be automatically converted
     * to `ImageBitmap` before upload to the GPU.
     *
     * For more information, have a look at the
     * [ImageBitmap](https://developer.mozilla.org/en-US/docs/Web/API/ImageBitmap) documentation.
     */
    loadUncompressedImagesAsBitmap = true;

    /**
     * Scene cache in scene manager.
     *
     * @hidden
     */
    _scenes: (Prefab | null)[] = [];

    /**
     * Currently active scene.
     *
     * @hidden
     */
    _scene: Scene;

    /** Incremented every time components are registered. @hidden */
    _registrationVersion: number = 0;

    /** Incremented every time a new main scene is loaded. @hidden */
    private _mainSceneVersion: number = 0;

    /** @hidden */
    private _textures!: TextureManager;
    /** @hidden */
    private _materials!: MaterialManager;
    /** @hidden */
    private _meshes!: MeshManager;
    /** @hidden */
    private _morphTargets!: ResourceManager<MorphTargets>;
    /** @hidden */
    private _fonts!: ResourceManager<Font>;
    /** @hidden */
    private _audioClips!: ResourceManager<AudioClip>;
    /** @hidden */
    private _particleEffects!: ParticleEffectManager;
    /** @hidden */
    private _pipelines!: PipelineManager;
    /** @hidden */
    private _probeVolumeScenarios!: ProbeVolumeScenarioManager;
    /**
     * Current loading uncompressed images.
     *
     * @note Indexed by js image index.
     *
     * @hidden
     */
    private _uncompressedPromises: Promise<void>[] = [];

    /**
     * WebAssembly bridge.
     *
     * @hidden
     */
    #wasm: WASM;

    /**
     * Physics manager, only available when physx is enabled in the runtime.
     *
     * @hidden
     */
    #physics: Physics | null = null;

    /**
     * WebXR library.
     *
     * @hidden
     */
    #webxr: WebXR;

    /**
     * Resize observer to track for canvas size changes.
     *
     * @hidden
     */
    #resizeObserver: ResizeObserver | null = null;

    /**
     * Whether a components bundle has already been loaded or not.
     *
     * @hidden
     */
    #bundleCache: Set<string> = new Set();

    /**
     * Job system watcher. Triggered when a job succeeds / fails.
     *
     * @hidden
     */
    #jobUpdate: Emitter<[number, JobStatus]> = new Emitter();

    /**
     * Create a new engine instance.
     *
     * @param wasm Wasm bridge instance
     * @param loadingScreen Loading screen .bin file data
     *
     * @hidden
     */
    constructor(wasm: WASM, loadingScreen: ArrayBuffer | null, withRenderer: boolean) {
        this.#wasm = wasm;
        this.#wasm['_setEngine'](this); /* String lookup to bypass private. */
        this.#wasm._loadingScreen = loadingScreen;

        const version = this.runtimeVersion;
        let versionString = `${version.major}.${version.minor}.${version.patch}`;
        if (version.rc > 0) {
            versionString += `-rc${version.rc}`;
        }
        this.log.info(LogTag.Engine, 'Wonderland Engine runtime version:', versionString);

        this.#webxr = new WebXR(this);

        checkRuntimeCompatibility(this.#wasm.runtimeVersion);

        /* Setup the error handler. This is used to to manage native errors. */
        this.#wasm._wl_set_error_callback(
            this.#wasm.addFunction((messagePtr: number) => {
                throw new Error(this.#wasm.UTF8ToString(messagePtr));
            }, 'vi')
        );

        this.#wasm._wl_application_create();

        this.#physics = null;

        if (this.#wasm.withPhysX) {
            this.#physics = new Physics(this);
        }

        this._init(withRenderer);

        this._scene = this._reload(0);

        this.#wasm._wl_set_jobUpdate_callback(
            this.#wasm.addFunction((jobId: number, status: JobStatus) => {
                this.#jobUpdate.notify(jobId, status);
            }, 'vii')
        );

        this.canvas.addEventListener(
            'webglcontextlost',
            (e) => this.log.error(LogTag.Engine, 'Context lost:', e),
            false
        );
    }

    /**
     * Start the engine if it's not already running.
     *
     * When using the {@link loadRuntime} function, this method is called
     * automatically.
     */
    start(): void {
        this.wasm._wl_application_start();
    }

    /**
     * Destroy the engine explicitly.
     * @since 1.4.6
     */
    destroy(): void {
        this.wasm._wl_application_exit();
        requestAnimationFrame(() => {
            this.wasm._wl_application_destroy();
        });
    }

    /**
     * Register a custom JavaScript component type.
     *
     * You can register a component directly using a class inheriting from {@link Component}:
     *
     * ```js
     * import { Component, Type } from '@wonderlandengine/api';
     *
     * export class MyComponent extends Component {
     *     static TypeName = 'my-component';
     *     static Properties = {
     *         myParam: {type: Type.Float, default: 42.0},
     *     };
     *     init() {}
     *     start() {}
     *     update(dt) {}
     *     onActivate() {}
     *     onDeactivate() {}
     *     onDestroy() {}
     * });
     *
     * // Here, we assume we have an engine already instantiated.
     * // In general, the registration occurs in the `index.js` file in your
     * // final application.
     * engine.registerComponent(MyComponent);
     * ```
     *
     * {@label CLASSES}
     * @param classes Custom component(s) extending {@link Component}.
     *
     * @since 1.0.0
     */
    registerComponent(...classes: ComponentConstructor[]) {
        for (const arg of classes) {
            this.wasm._registerComponent(arg);
        }
        ++this._registrationVersion;
    }

    /**
     * Register a component bundle
     *
     * The module must be of the form:
     *
     * ```js
     * export default function(engine) {
     *     engine.registerComponent(MyComponent);
     *     ...
     * }
     * ```
     *
     * Alternatively, you can manually register a component bundle:
     *
     * ```js
     * const registerBundle = (await import(url)).default;
     * registerBundle(engine);
     * ```
     *
     * @param url The URL of the module to register.
     * @param nocache If `true`, force browser to re-download the file.
     */
    async registerBundle(url: string, nocache = false) {
        if (!nocache && this.#bundleCache.has(url)) return;
        this.#bundleCache.add(url);

        if (nocache) url += `?t=${Date.now()}`;
        const register = (await import(url)).default;
        if (typeof register !== 'function') {
            throw new Error(
                `The bundle '${url}' doesn't have a default exported registration function`
            );
        }
        register(this);
    }

    /**
     * Update the loading screen progress bar.
     *
     * @param value Current loading percentage, in the range [0; 1].
     *
     * @since 1.2.1
     */
    setLoadingProgress(percentage: number) {
        this.wasm._wl_set_loading_screen_progress(clamp(percentage, 0, 1));
    }

    /**
     * Switch the current active scene.
     *
     * Once active, the scene will be updated and rendered on the canvas.
     *
     * The currently active scene is accessed via {@link WonderlandEngine.scene}:
     *
     * ```js
     * import {Component} from '@wonderlandengine/api';
     *
     * class MyComponent extends Component{
     *     start() {
     *         console.log(this.scene === this.engine.scene); // Prints `true`
     *     }
     * }
     * ```
     *
     * @note This method will throw if the scene isn't activatable.
     *
     * #### Component Lifecycle
     *
     * Marking a scene as active will:
     * * Call {@link Component#onDeactivate} for all active components of the previous scene
     * * Call {@link Component#onActivate} for all active components of the new scene
     *
     * #### Usage
     *
     * ```js
     * const scene = await engine.loadScene('Scene.bin');
     * engine.switchTo(scene);
     * ```
     *
     * @returns A promise that resolves once the scene is ready.
     *
     * @since 1.2.0
     */
    async switchTo(scene: Scene, opts: ActivateOptions = {}) {
        this.wasm._wl_deactivate_activeScene();

        /* Switch reference on engine **just** before activating, to allow
         * component to use `this.engine.scene` in `onActivate()`/`start()`. */
        const previous = this.scene;
        previous._postDeactivate();

        this._preactivate(scene);
        scene._activate();

        /* Enable/disable required views */
        this.#webxr.updateViewState(undefined);

        /* We loaded new views, update WebXR projection parameters */
        this.#webxr.updateProjectionParams(undefined, undefined);

        if (!this.onLoadingScreenEnd.isDataRetained) {
            this.onLoadingScreenEnd.notify();
        }

        const promises: Promise<unknown>[] = [];

        /* For now, we always automatically download dependencies for
         * the user, i.e., separate textures.bin and languages.bin.
         *
         * In the future, we can eventually expose this in the chunk API
         * to give more control to the user if needed.
         *
         * We do not make the user wait until the textures download is complete. */
        const promise = scene._downloadDependencies();
        promises.push(promise);

        const scenario = scene.probeVolumeScenario;
        if (scenario) {
            let scenarioPromise = scenario.loaded
                ? Promise.resolve(scenario)
                : this.probeVolumeScenarios.load('scenario.data.bin');
            scenarioPromise = scenarioPromise.then(
                (s) => (this.scene.probeVolumeScenario = s)
            );
            promises.push(scenarioPromise);
        }

        await this.i18n.setLanguage(this.i18n.languageCode(0));

        const {dispatchReadyEvent = false, waitForDependencies = false} = opts;

        if (waitForDependencies) await Promise.all(promises);

        this.onSceneActivated.notify(previous, scene);

        if (dispatchReadyEvent) scene.dispatchReadyEvent();
    }

    /**
     * Load the scene from a URL, as the main scene of a new {@link Scene}.
     *
     * #### Usage
     *
     * ```js
     * // The method returns the main scene
     * const scene = await engine.loadMainScene();
     * ```
     *
     * #### Destruction
     *
     * Loading a new main scene entirely resets the state of the engine, and destroys:
     * - All loaded scenes, prefabs, and gltf files
     * - Meshes
     * - Textures
     * - Materials
     *
     * @note This method can only load Wonderland Engine `.bin` files.
     *
     * @param options The URL pointing to the scene to load or an object
     *     holding additional loading options.
     * @param progress Optional progress callback. When setting a custom
     *     callback, you need to manually call {@link setLoadingProgress} to
     *     get progress updates in the loading screen.
     * @returns The main scene of the new {@link Scene}.
     */
    async loadMainScene(
        options: LoadOptions & ActivateOptions,
        progress?: ProgressCallback
    ) {
        /**
         * @todo: It's currently possible to put the runtime in a limbo
         * state using hot-reload: Multiple reload requests are sent, and scene loading
         * ends up being interleaved.
         *
         * Add a system to cancel the last loading, if any.
         */

        progress ??= (bytes: number, size: number) => {
            this.log.info(LogTag.Scene, `Scene downloading: ${bytes} / ${size}`);
            this.setLoadingProgress(bytes / size);
        };

        const opts = Prefab.makeUrlLoadOptions(options);
        const {streamed = true, nocache = false} = opts;
        if (streamed) {
            const options = await Scene.loadStream(opts, progress);
            const {stream, url} = Prefab.validateStreamOptions(options);
            return this._loadMainScene(stream, url, nocache, options as ActivateOptions);
        } else {
            const options = await Scene.loadBuffer(opts, progress);
            return this.loadMainSceneFromBuffer(options);
        }
    }

    /**
     * Similar to {@link WonderlandEngine.loadMainScene}, but loading is done
     * from an `ArrayBuffer`.
     *
     * @param options An object containing the buffer and extra metadata.
     * @returns The main scene of the new {@link Scene}.
     */
    async loadMainSceneFromBuffer(options: InMemoryLoadOptions & ActivateOptions) {
        const {nocache = false} = options;
        const {buffer, url} = Prefab.validateBufferOptions(options);
        return this._loadMainScene(buffer, url, nocache, options);
    }

    /**
     * Load a {@link Prefab} from a URL.
     *
     * #### Usage
     *
     * ```js
     * const prefab = await engine.loadPrefab('Prefab.bin');
     * ```
     *
     * @note This method can only load Wonderland Engine `.bin` files.
     * @note This method is a wrapper around {@link WonderlandEngine.loadPrefabFromBuffer}.
     *
     * @param url The URL pointing to the prefab to load.
     * @param progress Optional progress callback.
     * @returns The loaded {@link Prefab}.
     */
    async loadPrefab(options: LoadOptions, progress?: ProgressCallback) {
        const opts = Prefab.makeUrlLoadOptions(options);
        const {streamed = true} = opts;
        if (streamed) {
            const options = await Scene.loadStream(opts, progress);
            const scene = await this._loadSceneFromStream(Prefab, options);
            this._validateLoadedPrefab(scene);
            scene._initialize();
            return scene;
        } else {
            const options = await Scene.loadBuffer(opts, progress);
            return this.loadPrefabFromBuffer(options);
        }
    }

    /**
     * Similar to {@link WonderlandEngine.loadPrefab}, but loading is done from
     * an `ArrayBuffer`.
     *
     * @param options An object containing the buffer and extra metadata.
     * @returns A new loaded {@link Prefab}.
     */
    loadPrefabFromBuffer(options: InMemoryLoadOptions) {
        const scene = this._loadSceneFromBuffer(Prefab, options);
        this._validateLoadedPrefab(scene);
        scene._initialize();
        return scene;
    }

    /**
     * Load a scene from a URL.
     *
     * At the opposite of {@link WonderlandEngine.loadMainScene}, the scene loaded
     * will be added to the list of existing scenes, and its resources will be made
     * available for other scenes/prefabs/gltf to use.
     *
     * #### Resources Sharing
     *
     * Upon loading, the scene resources are added in the engine, and references
     * to those resources are updated.
     *
     * It's impossible for a scene loaded with this method to import pipelines.
     * Thus, the loaded scene will reference existing pipelines in the main scene,
     * based on their names.
     *
     * #### Usage
     *
     * ```js
     * const scene = await engine.loadScene('Scene.bin');
     * ```
     *
     * @note This method can only load Wonderland Engine `.bin` files.
     *
     * @param options The URL pointing to the scene to load or an object
     *     holding additional loading options.
     * @param progress Optional progress callback.
     * @returns A new loaded {@link Scene}.
     */
    async loadScene(options: LoadOptions, progress?: ProgressCallback) {
        const opts = Prefab.makeUrlLoadOptions(options);
        const {streamed = true} = opts;
        if (streamed) {
            const options = await Scene.loadStream(opts, progress);
            const scene = await this._loadSceneFromStream(Scene, options);
            this._validateLoadedScene(scene);
            scene._initialize();
            return scene;
        } else {
            const options = await Scene.loadBuffer(opts, progress);
            return this.loadSceneFromBuffer(options);
        }
    }

    /**
     * Create a glTF scene from a URL.
     *
     * @note Loading glTF files requires `enableRuntimeGltf` to be checked in
     *     the editor Project Settings.
     *
     * @note This method is a wrapper around {@link WonderlandEngine.loadGLTFFromBuffer}.
     *
     * @param options The URL pointing to the scene to load or an object
     *     holding additional loading options, optionally including
     *     {@link GLTFOptions}. {@link LoadOptions.streamed} is ignored as
     *     streamed glTF parsing isn't supported.
     * @param progress Optional progress callback.
     * @returns A new loaded {@link PrefabGLTF}.
     */
    async loadGLTF(opts: LoadOptions & GLTFOptions, progress?: ProgressCallback) {
        const memOptions = await Scene.loadBuffer(opts, progress);
        const options = isString(opts) ? memOptions : {...opts, ...memOptions};
        return this.loadGLTFFromBuffer(options);
    }

    /**
     * Similar to {@link WonderlandEngine.loadScene}, but loading is done from
     * an `ArrayBuffer`.
     *
     * @throws If the scene is streamable.
     *
     * @param options An object containing the buffer and extra metadata.
     * @returns A new loaded {@link Scene}.
     */
    loadSceneFromBuffer(options: InMemoryLoadOptions) {
        const scene = this._loadSceneFromBuffer(Scene, options);
        this._validateLoadedScene(scene);
        scene._initialize();
        return scene;
    }

    /** @overload */
    loadGLTFFromBuffer(
        options: InMemoryLoadOptions & GLTFOptions & {async: true}
    ): Promise<PrefabGLTF>;
    /** @overload */
    loadGLTFFromBuffer(
        options: InMemoryLoadOptions & GLTFOptions & {async?: false}
    ): PrefabGLTF;
    /**
     * Similar to {@link WonderlandEngine.loadGLTF}, but loading is done from
     * an `ArrayBuffer`.
     *
     * @note Loading glTF files requires `enableRuntimeGltf` to be checked in
     *     the editor Project Settings.
     *
     * @param options An object containing the buffer and extra glTF metadata.
     * @returns A new loaded {@link PrefabGLTF}.
     */
    loadGLTFFromBuffer(
        options: InMemoryLoadOptions & GLTFOptions
    ): Promise<PrefabGLTF> | PrefabGLTF;
    loadGLTFFromBuffer(
        options: InMemoryLoadOptions & GLTFOptions
    ): Promise<PrefabGLTF> | PrefabGLTF {
        Scene.validateBufferOptions(options);
        /** @todo(2.0.0): Make async `true` by default */
        const {
            buffer,
            extensions = false,
            generateSimpleTangents = false,
            async = false,
        } = options;

        const wasm = this.wasm;

        if (!wasm._wl_glTF_scene_create) {
            throw new Error(
                'Loading .gltf files requires `enableRuntimeGltf` to be checked in the editor Project Settings.'
            );
        }

        const ptr = wasm.copyBufferToHeap(buffer);
        let index: number | null = null;
        try {
            index = wasm._wl_glTF_scene_create(
                extensions,
                generateSimpleTangents,
                async,
                ptr,
                buffer.byteLength
            );
        } catch (e) {
            /* Catch calls to abort(), e.g. via asserts */
            wasm._free(ptr);
            throw e;
        }

        if (!index) {
            wasm._free(ptr);
            throw new Error('Internal gltf loading error');
        }

        if (async) {
            const jobId = SceneResource._unpackId(index);
            const sceneIndex = SceneResource._unpackSceneIndex(index);
            return this._awaitJob(jobId)
                .then(() => {
                    return this._createPrefabGLTF(sceneIndex);
                })
                .finally(() => {
                    wasm._free(ptr);
                });
        }

        /** @todo(2.0.0): Remove the sync nature of this method if not required. */
        wasm._free(ptr);
        return this._createPrefabGLTF(index);
    }

    /**
     * Checks whether the given component is registered or not.
     *
     * @param typeOrClass A string representing the component typename (e.g., `'cursor-component'`),
     *     or a component class (e.g., `CursorComponent`).
     * @returns `true` if the component is registered, `false` otherwise.
     */
    isRegistered(typeOrClass: string | ComponentConstructor) {
        return this.#wasm.isRegistered(
            isString(typeOrClass) ? typeOrClass : typeOrClass.TypeName
        );
    }

    /**
     * Retrieve the registered component from its type name.
     *
     * @param typename The component {@link Component.TypeName} property
     * @returns The class if a component was registered with given name, `null` otherwise.
     */
    getComponentClass<T extends Component>(
        typename: string
    ): ComponentConstructor<T> | null {
        const index = this.wasm._componentTypeIndices[typename];
        if (index === undefined) return null;
        return this.wasm._componentTypes[index] as ComponentConstructor<T>;
    }

    /**
     * Resize the canvas and the rendering context.
     *
     * @note The `width` and `height` parameters will be scaled by the
     * `devicePixelRatio` value. By default, the pixel ratio used is
     * [window.devicePixelRatio](https://developer.mozilla.org/en-US/docs/Web/API/Window/devicePixelRatio).
     *
     * @param width The width, in CSS pixels.
     * @param height The height, in CSS pixels.
     * @param devicePixelRatio The pixel ratio factor.
     */
    resize(width: number, height: number, devicePixelRatio = window.devicePixelRatio) {
        width = width * devicePixelRatio;
        height = height * devicePixelRatio;
        this.canvas.width = width;
        this.canvas.height = height;
        this.wasm._wl_application_resize(width, height);
        this.onResize.notify();
    }

    /**
     * Run the next frame.
     *
     * @param fixedDelta The elapsed time between this frame and the previous one.
     *
     * @note The engine automatically schedules next frames. You should only
     * use this method for testing.
     */
    nextFrame(fixedDelta: number = 0) {
        this.#wasm._wl_nextFrame(fixedDelta);
    }

    /**
     * Request an XR session.
     *
     * @note Please use this call instead of directly calling `navigator.xr.requestSession()`.
     * Wonderland Engine requires to be aware that a session is started, and this
     * is done through this call.
     *
     * @param mode The XR mode.
     * @param features An array of required features, e.g., `['local-floor', 'hit-test']`.
     * @param optionalFeatures An array of optional features, e.g., `['bounded-floor', 'depth-sensing']`.
     * @returns A promise resolving with the `XRSession`, a string error message otherwise.
     */
    requestXRSession(
        mode: XRSessionMode,
        features: string[],
        optionalFeatures: string[] = []
    ): Promise<XRSession> {
        return this.#webxr.requestSession(mode, features, optionalFeatures);
    }

    /**
     * Offer an XR session.
     *
     * Adds an interactive UI element to the browser interface to start an XR
     * session. Browser support is optional, so it's advised to still allow
     * requesting a session with a UI element on the website itself.
     *
     * @note Please use this call instead of directly calling `navigator.xr.offerSession()`.
     * Wonderland Engine requires to be aware that a session is started, and this
     * is done through this call.
     *
     * @param mode The XR mode.
     * @param features An array of required features, e.g., `['local-floor', 'hit-test']`.
     * @param optionalFeatures An array of optional features, e.g., `['bounded-floor', 'depth-sensing']`.
     * @returns A promise resolving with the `XRSession`, a string error message otherwise.
     *
     * @since 1.1.5
     */
    offerXRSession(
        mode: XRSessionMode,
        features: string[],
        optionalFeatures: string[] = []
    ): Promise<XRSession> {
        return this.#webxr.offerSession(mode, features, optionalFeatures);
    }

    /**
     * Wrap an object ID using {@link Object}.
     *
     * @note This method performs caching and will return the same
     * instance on subsequent calls.
     *
     * @param objectId ID of the object to create.
     * @returns The object
     *
     * @deprecated Use {@link Scene#wrap} instead.
     *
     */
    wrapObject(objectId: number): Object3D {
        return this.scene.wrap(objectId);
    }

    toString() {
        return 'engine';
    }

    /* Public Getters & Setter */

    /** Currently active scene. */
    get scene(): Scene {
        return this._scene;
    }

    /**
     * WebAssembly bridge.
     *
     * @note Use with care. This object is used to communicate
     * with the WebAssembly code throughout the api.
     *
     * @hidden
     */
    get wasm(): WASM {
        return this.#wasm;
    }

    /**
     * WebXR api.
     *
     * @hidden
     */
    get webxr(): WebXR {
        return this.#webxr;
    }

    /** Canvas element that Wonderland Engine renders to. */
    get canvas(): HTMLCanvasElement {
        return this.#wasm.canvas;
    }

    /**
     * Current WebXR session or `null` if no session active.
     *
     * @deprecated Use {@link XRSessionState.session} on the {@link xr}
     * object instead.
     */
    get xrSession(): XRSession | null {
        return this.xr?.session ?? null;
    }

    /**
     * Current WebXR frame or `null` if no session active.
     *
     * @deprecated Use {@link XRSessionState.frame} on the {@link xr}
     * object instead.
     */
    get xrFrame(): XRFrame | null {
        return this.xr?.frame ?? null;
    }

    /**
     * Current WebXR base layer or `null` if no session active.
     *
     * @deprecated Use {@link XRSessionState.baseLayer} on the {@link xr}
     * object instead.
     */
    get xrBaseLayer(): XRProjectionLayer | XRWebGLLayer | null {
        return this.xr?.baseLayer ?? null;
    }

    /**
     * Current WebXR framebuffer or `null` if no session active.
     *
     * @deprecated Use {@link XRSessionState.framebuffers} on the {@link xr}
     * object instead.
     */
    get xrFramebuffer(): WebGLFramebuffer | null {
        return this.xr?.framebuffers[0] ?? null;
    }

    /**
     * WebXR framebuffer scale factor.
     */
    get xrFramebufferScaleFactor() {
        return this.#webxr.framebufferScaleFactor;
    }

    set xrFramebufferScaleFactor(value: number) {
        this.#webxr.framebufferScaleFactor = value;
    }

    /** Physics manager, only available when physx is enabled in the runtime. */
    get physics() {
        return this.#physics;
    }

    /** Texture resources */
    get textures() {
        return this._textures;
    }

    /** Material resources */
    get materials() {
        return this._materials;
    }

    /** Mesh resources */
    get meshes() {
        return this._meshes;
    }

    /** Morph target set resources */
    get morphTargets() {
        return this._morphTargets;
    }

    /** Font resources */
    get fonts() {
        return this._fonts;
    }

    /** Particle effect resources */
    get particleEffects() {
        return this._particleEffects;
    }

    /** Pipeline resources */
    get pipelines() {
        return this._pipelines;
    }

    /** Probe volume scenarios resources */
    get probeVolumeScenarios() {
        return this._probeVolumeScenarios;
    }

    /** AudioClip resources */
    get audioClips() {
        return this._audioClips;
    }

    /** Get all uncompressed images. */
    get images(): ImageLike[] {
        const wasm = this.wasm;
        const max = wasm._tempMemSize >> 1;
        const count = wasm._wl_get_images(wasm._tempMem, max);
        const result = new Array(count);
        for (let i = 0; i < count; ++i) {
            const index = wasm._tempMemUint16[i];
            result[i] = wasm._images[index];
        }
        return result;
    }

    /**
     * Promise that resolve once all uncompressed images are loaded.
     *
     * This is equivalent to calling {@link WonderlandEngine.images}, and wrapping each
     * `load` listener into a promise.
     */
    get imagesPromise(): Promise<ImageLike[]> {
        const wasm = this.wasm;
        const max = wasm._tempMemSize >> 1;
        const count = wasm._wl_get_images(wasm._tempMem, max);
        const result = new Array(count);
        for (let i = 0; i < count; ++i) {
            const index = wasm._tempMemUint16[i];
            const loading = this._uncompressedPromises[index];
            result[i] = (loading ?? Promise.resolve()).then(() => wasm._images[index]);
        }
        return Promise.all(result);
    }

    /**
     * `true` if the texture streaming is currently idle, i.e.,
     * not attempting to upload any textures.
     *
     * @hidden
     */
    get isTextureStreamingIdle() {
        return !!this.wasm._wl_renderer_streaming_idle();
    }

    /**
     * `true` if reverse-Z rendering is supported and enabled.
     *
     * @hidden
     */
    get isReverseZEnabled() {
        return !!this.wasm._wl_renderer_isReverseZEnabled();
    }

    /*
     * Enable or disable the mechanism to automatically resize the canvas.
     *
     * Internally, the engine uses a [ResizeObserver](https://developer.mozilla.org/en-US/docs/Web/API/ResizeObserver).
     * Changing the canvas css will thus automatically be tracked by the engine.
     */
    set autoResizeCanvas(flag: boolean) {
        const state = !!this.#resizeObserver;
        if (state === flag) return;

        if (!flag) {
            this.#resizeObserver?.unobserve(this.canvas);
            this.#resizeObserver = null;
            return;
        }
        this.#resizeObserver = new ResizeObserver((entries) => {
            for (const entry of entries) {
                if (entry.target === this.canvas) {
                    this.resize(entry.contentRect.width, entry.contentRect.height);
                }
            }
        });
        this.#resizeObserver.observe(this.canvas);
    }

    /** `true` if the canvas is automatically resized by the engine. */
    get autoResizeCanvas() {
        return this.#resizeObserver !== null;
    }

    /** Retrieves the runtime version. */
    get runtimeVersion(): Version {
        return this.#wasm.runtimeVersion;
    }

    /** Engine {@link Logger}. Use it to turn on / off logging. */
    get log(): Logger {
        return this.#wasm._log;
    }

    /**
     * Graphics API used for rendering.
     *
     * @since 1.5.0
     */
    get graphicsApi(): GraphicsApi {
        return this.#wasm.preinitializedWebGPUDevice !== null
            ? GraphicsApi.WebGPU
            : GraphicsApi.WebGL2;
    }

    /* Internal-Only Methods */

    /**
     * Initialize the engine.
     *
     * @note Should be called after the WebAssembly is fully loaded.
     *
     * @hidden
     */
    _init(withRenderer: boolean): boolean {
        const success = this.#wasm._wl_application_init(withRenderer);
        if (!success) {
            throw new Error(`Failed to initializing Wonderland runtime`);
        }

        this.resize(this.canvas.clientWidth, this.canvas.clientHeight);
        return true;
    }

    /**
     * Reset the runtime state, including:
     *     - Component cache
     *     - Images
     *     - Callbacks
     *
     * @note This api is meant to be used internally.
     *
     * @hidden
     */
    _reset() {
        this.wasm.reset();
        this._scenes.length = 0;
        this._scene = this._reload(0);
        this.#bundleCache.clear();
        this._registrationVersion = 0;
        ++this._mainSceneVersion;
        return this.switchTo(this._scene);
    }

    /**
     * Attempt to hot reload the engine.
     *
     * @param filename URL of the file to use for the reload.
     *     If `null`, forces a full page reload.
     *
     * @hidden
     */
    async _reloadRequest(filename: string | null) {
        if (filename === null) {
            reloadPage(this);
            return;
        }

        try {
            if (this.autoHotReload)
                await this.loadMainScene({url: filename, nocache: true});
        } catch (e) {
            console.error('Hot reload request failed to load main scene, reason:', e);
            setTimeout(() => reloadPage(this), 1000);
            return;
        }

        this.onHotReloadRequest.notify(filename);
    }

    /**
     * Add an empty scene.
     *
     * @returns The newly created scene
     *
     * @hidden
     */
    _createEmpty(): Scene {
        const wasm = this.wasm;
        const index = wasm._wl_scene_create_empty();
        const scene = new Scene(this, index);
        this._scenes[index] = scene;
        return scene;
    }

    /** @hidden */
    _destroyScene(instance: Prefab) {
        const wasm = this.wasm;
        wasm._wl_scene_destroy(instance._index);

        const index = instance._index;
        (instance._index as number) = -1;
        if (this.erasePrototypeOnDestroy) {
            Object.setPrototypeOf(instance, DestroyedPrefabInstance);
        }

        this._scenes[index] = null;
    }

    /**
     * Download and apply queued dependency files (.bin).
     *
     * @hidden
     */
    async _loadResourcesBin(url: string) {
        const sink = new ChunkedSceneLoadSink(this, SceneType.Dependency, url);
        const stream = await fetchStreamWithProgress(url);
        return stream.pipeTo(new WritableStream(sink));
    }

    /**
     * Reload the state of the engine with a new main scene.
     *
     * @param index Scene index.
     *
     * @hidden
     */
    private _reload(index: number) {
        const scene = new Scene(this, index);
        this._scenes[index] = scene;

        this._textures = new TextureManager(this);
        this._materials = new MaterialManager(this);
        this._meshes = new MeshManager(this);
        this._morphTargets = new ResourceManager(this, MorphTargets);
        this._fonts = new ResourceManager(this, Font);
        this._audioClips = new ResourceManager(this, AudioClip);
        this._particleEffects = new ParticleEffectManager(this);
        this._pipelines = new PipelineManager(this);
        this._probeVolumeScenarios = new ProbeVolumeScenarioManager(this);
        this._uncompressedPromises.length = 0;

        return scene;
    }

    /**
     * Helper to load a main scene from an `ArrayBuffer` or `ReadableStream`.
     *
     * @param data Buffer or stream.
     * @param url Base URL.
     * @param nocache If `true`, force a browser reload of files to download.
     *     Only affects components loading for now.
     * @param options Activation options.
     * @returns The loaded main scene.
     *
     * @hidden
     */
    private async _loadMainScene(
        data: ArrayBuffer | ReadableStream<Uint8Array>,
        url: string,
        nocache: boolean,
        options: ActivateOptions
    ) {
        if (nocache) url += `?t=${Date.now()}`;

        const wasm = this.#wasm;

        /*
         * - Deactivate currently active scene
         * - Destroy all scenes
         * - Mark all resources as destroyed
         * - Activation of main scene to prevent the runtime to be in limbo
         */

        const isLoadingScreen = this._mainSceneVersion === 0;
        ++this._mainSceneVersion;

        /* If this is the loading screen, keep it active until the first main
         * scene got loaded. Otherwise the progress bar won't update. This
         * assumes the loading screen has no components that need onDestroy(). */
        if (!isLoadingScreen) {
            wasm._wl_deactivate_activeScene();

            /* Only destroy all scenes once the current active one is disabled */
            for (let i = this._scenes.length - 1; i >= 0; --i) {
                const scene = this._scenes[i];
                if (scene) scene.destroy();
            }
        }

        /* Mark all resources as destroyed */
        this._textures._clear();
        this._materials._clear();
        this._meshes._clear();
        this._morphTargets._clear();
        this._uncompressedPromises.length = 0;
        this._registrationVersion = 0;

        const stream =
            data instanceof ReadableStream
                ? data
                : new ReadableStream(new ArrayBufferSource(data));
        const sink = new ChunkedSceneLoadSink(this, SceneType.Main, url);
        await stream.pipeTo(new WritableStream(sink));

        const mainScene = this._reload(sink.sceneIndex);
        /* Start downloading uncompressed textures as soon as possible */
        this._loadUncompressedImages(mainScene._index);

        /**
         * @todo(2.0.0)
         *
         * Backward compatibility: We need to set this instance reference on the
         * engine **before** component creation occurs, since users have been accessing
         * the scene with `this.engine.scene` in `init()`.
         */
        this._preactivate(mainScene);

        const componentsBundle = mainScene.componentsBundle;
        if (componentsBundle) {
            /* Relative URL are resolved from the script that imports, and **not** from
             * the document. We assume that the components bundle is located alongside
             * other resources, relative to the .bin.
             *
             * @note: `componentsBundle` already contains the same baseURL as the .bin. */
            const bundleURL = new URL(componentsBundle, document.baseURI);
            const url = nocache ? bundleURL.href.split('?')[0] : bundleURL.href;
            await this.registerBundle(url, nocache);
        }
        mainScene._initialize();

        await this.switchTo(mainScene, options);

        return mainScene;
    }

    /**
     * Helper to load prefab and activatable scene from an `ArrayBuffer`.
     *
     * @param PrefabClass Scene constructor.
     * @param options Loading options.
     * @returns The loaded prefab.
     *
     * @hidden
     */
    private _loadSceneFromBuffer<T extends Prefab>(
        PrefabClass: Constructor<T>,
        options: InMemoryLoadOptions
    ) {
        const {buffer, url} = Scene.validateBufferOptions(options);

        const sink = new ChunkedSceneLoadSink(this, SceneType.Prefab, url);
        sink.write(new Uint8Array(buffer));
        sink.close();
        const index = sink.sceneIndex;

        /* The scene was successfully loaded on the wasm side, we need to
         * add it to the scene cache, since the wasm might read it. */
        const scene = new PrefabClass(this, index);
        this._scenes[index] = scene;
        /* Start downloading uncompressed textures as soon as possible */
        this._loadUncompressedImages(scene._index);

        return scene;
    }

    /**
     * Helper to load prefab and activatable scene from a `ReadableStream`.
     *
     * @param PrefabClass Scene constructor.
     * @param options Loading options.
     * @returns The loaded prefab.
     *
     * @hidden
     */
    private async _loadSceneFromStream<T extends Prefab>(
        PrefabClass: Constructor<T>,
        options: StreamLoadOptions
    ) {
        const {stream, url} = Scene.validateStreamOptions(options);
        const sink = new ChunkedSceneLoadSink(this, SceneType.Prefab, url);
        await stream.pipeTo(new WritableStream(sink));
        const index = sink.sceneIndex;

        /* The scene was successfully loaded on the wasm side, we need to
         * add it to the scene cache, since the wasm might read it. */
        const scene = new PrefabClass(this, index);
        this._scenes[index] = scene;
        /* Start downloading uncompressed textures as soon as possible */
        this._loadUncompressedImages(scene._index);

        return scene;
    }

    /**
     * Checks if the loaded scene is a prefab and throws an error if not.
     *
     * @param scene The loaded scene
     *
     * @hidden
     */
    private _validateLoadedPrefab(scene: Prefab) {
        if (this.wasm._wl_scene_activatable(scene._index)) {
            this.wasm._wl_scene_destroy(scene._index);
            throw new Error(
                'File is not a prefab. To load a scene, use loadScene() instead'
            );
        }
    }

    /**
     * Checks if the loaded scene is a scene (and not a prefab) and throws an
     * error if not.
     *
     * @param scene The loaded scene
     *
     * @hidden
     */
    private _validateLoadedScene(scene: Scene) {
        if (!this.wasm._wl_scene_activatable(scene._index)) {
            this.wasm._wl_scene_destroy(scene._index);
            throw new Error(
                'File is not a scene. To load a prefab, use loadPrefab() instead'
            );
        }
    }

    /**
     * Set the scene data on the engine, before the activation occurs.
     *
     * @hidden
     */
    private _preactivate(scene: Scene) {
        this._scene = scene;
        /* Update the current active scene in the physx manager */
        if (this.physics) (this.physics._hit._scene as Scene) = scene;
    }

    /**
     * Finish GLTF prefab creation
     *
     * @param index Scene index
     * @returns The GLTF prefab
     *
     * @hidden
     */
    private _createPrefabGLTF(index: number): PrefabGLTF {
        const scene = new PrefabGLTF(this, index);
        this._scenes[scene._index] = scene;
        /* Start downloading uncompressed textures as soon as possible */
        this._loadUncompressedImages(scene._index);
        return scene;
    }

    /**
     * Notify the runtime when images are ready to be uploaded.
     *
     * @param sceneIndex Index of the scene where the image originated
     * @hidden
     */
    private _loadUncompressedImages(sceneIndex: number) {
        const mainSceneVersion = this._mainSceneVersion;
        const wasm = this.wasm;

        const bitmapOptions: ImageBitmapOptions = {
            colorSpaceConversion: 'none',
            imageOrientation: 'flipY',
        };

        const imageCount = wasm._wl_resource_count(ResourceType.Image);
        for (let i = 1; i < imageCount; ++i) {
            if (wasm._wl_resource_originScene(ResourceType.Image, i) !== sceneIndex)
                continue;

            const jsImageIndex = wasm._wl_image_get_jsImage_index(i);
            if (!jsImageIndex) continue;

            const src = wasm._imageSources[jsImageIndex];
            if (!src) continue;

            const promise = this.loadUncompressedImagesAsBitmap
                ? TextureManager.fetchImageBitmap(src, bitmapOptions)
                : TextureManager.fetchImage(src);

            promise.finally(() => {
                if (this._mainSceneVersion !== mainSceneVersion) return;
                /* Image source can safely be discarded */
                wasm._imageSources[jsImageIndex] = null;
            });

            this._uncompressedPromises[jsImageIndex] = promise
                .then((img) => {
                    /* Discard the promise if the main scene has been reloaded */
                    if (this._mainSceneVersion !== mainSceneVersion) return;
                    wasm._images[jsImageIndex] = img;
                    wasm._wl_image_markReady(i, img.width, img.height, needsFlipY(img));
                })
                .catch((e) => {
                    /* Discard the promise if the main scene has been reloaded */
                    if (this._mainSceneVersion !== mainSceneVersion) return;
                    this.log.error(LogTag.Scene, 'Failed to load uncompressed image', e);
                });
        }
    }

    /**
     * Await a job from the job system.
     *
     * @param targetJobId Job id to wait for.
     * @returns A promise that resolves once the job succeeds or fails.
     */
    private _awaitJob(targetJobId: number): Promise<void> {
        return new Promise((resolve, reject) => {
            const onJobUpdate = (id: number, status: JobStatus) => {
                if (id !== targetJobId) return;
                this.#jobUpdate.remove(onJobUpdate);
                if (status === JobStatus.Success) {
                    resolve();
                } else {
                    reject();
                }
            };
            this.#jobUpdate.add(onJobUpdate);
        });
    }
}
